import React, { useState } from "react";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Snackbar from "@mui/material/Snackbar";
import CloseIcon from "@mui/icons-material/Close";
import FavoriteIcon from "@mui/icons-material/Favorite";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DonationOption from "../components/Donate/DonationOption";

const Donate = () => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setOpen(true);
    });
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  const buttonStyles = {
    bgcolor: theme.palette.donate.primary,
    color: theme.palette.common.black,
    width: "100%",
    minHeight: 48,
    "&:hover": {
      backgroundColor: theme.palette.donate.secondary,
      transform: "translateY(-1px)",
    },
    transition: "all 180ms ease",
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: theme.palette.primary.dark,
        background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
        color: theme.palette.common.white,
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: "auto", px: { xs: 2, sm: 4 }, py: 7 }}>
        <Box sx={{ textAlign: "center", maxWidth: 800, mx: "auto", mb: 4 }}>
          <Chip
            label="Support the work"
            sx={{
              bgcolor: "rgba(255,255,255,0.1)",
              color: theme.palette.common.white,
              border: "1px solid rgba(255,255,255,0.2)",
              mb: 2,
              fontWeight: 800,
            }}
          />
          <Typography
            variant="h2"
          component="h1"
            sx={{
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontStyle: "italic",
              fontWeight: 500,
              fontSize: { xs: "2.5rem", md: "4rem" },
            }}
          >
            Donate
          </Typography>
          <Typography
            sx={{
              mt: 2,
              color: "rgba(255,255,255,0.84)",
              lineHeight: 1.8,
              fontSize: { xs: "1rem", md: "1.08rem" },
            }}
          >
            Donations help fund specialist reconstructive surgery, equipment,
            consumables and training with partner teams. Cyrenaeus is run on a
            voluntary basis so gifts can be focused on patient care and lasting
            clinical capacity.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: 2.5,
            alignItems: "stretch",
          }}
        >
          <DonationOption
            name="PayPal Giving Fund"
            content={
              <Box>
                <Typography sx={{ color: "rgba(255,255,255,0.84)", mb: 2 }}>
                  Donate via the PayPal Giving Fund. Cyrenaeus does not pay fees
                  through this route, and you can add Gift Aid once you have
                  donated.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<FavoriteIcon />}
                  onClick={() => {
                    window.open(
                      "https://www.paypal.com/gb/fundraiser/charity/4826330",
                      "_blank"
                    );
                  }}
                  sx={buttonStyles}
                >
                  Donate via PayPal
                </Button>
              </Box>
            }
          />
          <DonationOption
            name="Stripe"
            content={
              <Box>
                <Typography sx={{ color: "rgba(255,255,255,0.84)", mb: 2 }}>
                  Stripe accepts major credit and debit cards, plus Apple Pay and
                  Google Pay. Fees apply, but it is a quick option for card
                  donations.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<CreditCardIcon />}
                  onClick={() => {
                    window.open(
                      "https://donate.stripe.com/7sI9E0g5L0tx8Zq7ss",
                      "_blank"
                    );
                  }}
                  sx={buttonStyles}
                >
                  Donate via Stripe
                </Button>
              </Box>
            }
          />
          <DonationOption
            name="Bank Transfer"
            content={
              <Box>
                <Typography sx={{ color: "rgba(255,255,255,0.84)", mb: 2 }}>
                  Prefer a direct transfer? Copy the details below and use your
                  name as the reference where possible.
                </Typography>
                {[
                  { id: "account-number", label: "Account Number", value: "68824095" },
                  { id: "sort-code", label: "Sort Code", value: "08-92-50" },
                  { id: "iban", label: "IBAN", value: "GB34CPBK08925068824095" },
                  { id: "bic", label: "BIC", value: "CPBKGB21CCB" },
                ].map((option) => (
                  <TextField
                    key={option.id}
                    fullWidth
                    label={option.label}
                    value={option.value}
                    variant="standard"
                    sx={{
                      mb: 1.25,
                      "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.72)" },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: theme.palette.common.white,
                      },
                      "& .MuiInputBase-input": {
                        color: theme.palette.common.white,
                      },
                      "& .MuiInput-underline:before": {
                        borderBottomColor: "rgba(255,255,255,0.42)",
                      },
                      "& .MuiInput-underline:hover:before": {
                        borderBottomColor: theme.palette.common.white,
                      },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: theme.palette.donate.primary,
                      },
                    }}
                    slotProps={{
                      input: {
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={`Copy ${option.label}`}
                              onClick={() => handleCopy(option.value)}
                              edge="end"
                              sx={{ color: theme.palette.common.white }}
                            >
                              <ContentCopyIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                ))}
              </Box>
            }
          />
        </Box>

        <Box
          sx={{
            mt: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            color: "rgba(255,255,255,0.78)",
          }}
        >
          <AccountBalanceIcon sx={{ color: theme.palette.donate.primary }} />
          <Typography variant="body2">
            UK registered charity · Charity number 1202741
          </Typography>
        </Box>
      </Box>
      <Snackbar
        open={open}
        autoHideDuration={2000}
        onClose={handleClose}
        message="Copied"
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
};

export default Donate;
